<template>
	<div class="alerts-container">
		<header
			class="alerts-heading-container"
			aria-label="Header area for dashboard to-do alerts"
			v-if="isToDo"
		>
			<h1 class="alerts-title">
				Your To-Do List
				<span
					class="alerts-counter"
					v-if="
						statusTotals.toDoAlertCount && hasAccessToClientConnect
					"
					>{{ statusTotals.toDoAlertCount }}</span
				>
			</h1>
			<p class="alerts-description">
				Don't miss an opportunity, take action on the alerts below from
				<router-link
					:to="'/client-connect-v2/alerts'"
					class="alerts-link"
					>Client Connect</router-link
				>.
			</p>
		</header>
		<header
			class="alerts-heading-container"
			aria-label="Header area for dashboard done for you alerts"
			v-if="isDoneForYou"
		>
			<h1 class="alerts-title">
				Done For You
				<span
					class="alerts-counter"
					v-if="
						statusTotals.toDoAlertCount && hasAccessToClientConnect
					"
					>{{ statusTotals.doneForYouAlertCount }}</span
				>
			</h1>
			<p class="alerts-description">
				These are action items that were done on your behalf by UWM in
				<router-link
					:to="'/client-connect-v2/alerts'"
					class="alerts-link"
					>Client Connect</router-link
				>.
			</p>
		</header>
		<div v-if="isLoading && !error">
			<FallBack>
				<LoadingIcon />
			</FallBack>
		</div>
		<div
			v-if="
				!error &&
				!isLoading &&
				noAlertsFound &&
				isToDo &&
				hasAccessToClientConnect
			"
		>
			<FallBack iconStyle="far" icon="bell">
				<div slot>
					<div class="no-alerts-message">
						You have no alerts to take action on.
						<router-link
							:to="'/preference-center/alerts'"
							class="alerts-link"
							>Configure your alert settings</router-link
						>.
					</div>
				</div>
			</FallBack>
		</div>
		<div
			v-if="
				!error &&
				!isLoading &&
				noAlertsFound &&
				isDoneForYou &&
				hasAccessToClientConnect
			"
		>
			<FallBack iconStyle="far" icon="bell">
				<div slot>
					<div class="no-alerts-message">
						We've taken no automated actions in the past 30 days.
						<router-link
							:to="'/preference-center/alerts'"
							class="alerts-link"
							>Configure your alert settings</router-link
						>.
					</div>
				</div>
			</FallBack>
		</div>
		<Table
			v-if="
				!isLoading &&
				!error &&
				!isEmpty(dashboardAlerts) &&
				hasAccessToClientConnect
			"
			class="table"
			aria-label="Alerts list"
		>
			<thead class="table-head">
				<tr class="table-head-row">
					<th
						v-for="(columnHeader, index) in columnHeaders"
						:key="index"
						scope="col"
						:class="`table-head__${columnHeader.className}`"
					>
						{{ columnHeader.name }}
					</th>
					<th scope="col" class="table-head__quick-actions">
						<span class="quick-actions">Quick actions</span>
					</th>
				</tr>
			</thead>
			<tbody class="table-body">
				<tr
					class="table-body-row"
					v-for="(alert, index) in dashboardAlerts"
					:key="index"
				>
					<td class="table-body__type">
						<div class="alert-icon">
							<AlertTypeIcon :alertType="alert.type" />
						</div>
						<div class="alert-type">
							<span
								role="button"
								aria-label="Alert type"
								class="type"
								@click="
									$emit(
										'alertClicked',
										alert.lead,
										alert.alertId,
										alert.status,
									)
								"
							>
								{{ alert.type }}
							</span>
							<span>
								{{
									alert.type === 'Birthday'
										? getDateString(
												alert.alertDateTime,
												alert.type,
										  )
										: alert.value
								}}</span
							>
						</div>
					</td>
					<td class="table-body__contact">
						<ContactIcon
							:firstName="alert.lead.firstName"
							:lastName="alert.lead.lastName"
						/>
						<div
							class="alert-type-info b360-handle-multiline-overflow"
						>
							<span>
								<TextButton
									aria-label="Contact Full Name"
									@onClick="$emit('nameClicked', alert.lead)"
									:title="
										truncateModalFields({
											fieldName: `${alert.lead.firstName} ${alert.lead.lastName}`,
											fieldLength: 20,
											attributeName: 'title',
										})
									"
								>
									{{
										`${alert.lead.firstName} ${alert.lead.lastName}`
									}}
								</TextButton>
							</span>
							<span>{{ alert.lead.type }}</span>
						</div>
					</td>
					<td class="table-body__date">
						{{ getDateString(alert.alertDateTime) }}
					</td>
					<td class="table-body__status">
						<AlertStatus :status="alert.status" />
					</td>
					<td class="table-body__quick-actions">
						<QuickActionsMenu
							:data-tour="
								isToDo && index === 0
									? 'cc-dashboard-quick-actions'
									: ''
							"
							:lightButton="true"
							:lightButtonIcon="['far', 'ellipsis-h']"
							:status="alert.status"
							:menus="populateQuickActions(alert)"
							:hasDeleteOption="false"
							@sendToBlink="
								$emit('sendToBlinkClicked', alert.lead)
							"
							@viewAlert="
								$emit('alertClicked', alert.lead, alert.alertId)
							"
							@viewContact="$emit('nameClicked', alert.lead)"
							@viewInEase="openInEase(alert.loan.easeLoanUrl)"
							@sendEmail="$emit('sendEmailClicked', alert)"
							@addNote="
								$emit(
									'addNoteClicked',
									alert.lead,
									alert.alertId,
								)
							"
							@markAsToDo="
								$emit(
									'markAsClicked',
									STATUS_ACTION_TYPES.TO_DO,
									alert.alertId,
								)
							"
							@markAsDone="
								$emit(
									'markAsClicked',
									STATUS_ACTION_TYPES.DONE,
									alert.alertId,
								)
							"
							@markAsDismissed="
								$emit(
									'markAsClicked',
									STATUS_ACTION_TYPES.DISMISSED,
									alert.alertId,
								)
							"
						/>
					</td>
				</tr>
			</tbody>
		</Table>
		<AccessDenied
			class="access-denied"
			v-if="!isLoading && !error && !hasAccessToClientConnect"
			:item="'Client Connect'"
		/>
		<router-link
			v-if="isToDo && !noAlertsFound && hasAccessToClientConnect"
			tag="button"
			:to="`/client-connect-v2/alerts/Alert`"
			:class="`b360-btn b360-btn--secondary view-all`"
			:aria-label="`View all new and to-do alerts`"
			>View All
		</router-link>
		<router-link
			v-if="isDoneForYou && !noAlertsFound && hasAccessToClientConnect"
			tag="button"
			:to="`/client-connect-v2/alerts/Email`"
			:class="`b360-btn b360-btn--secondary view-all`"
			:aria-label="`View all done for you alerts`"
			>View All
		</router-link>
	</div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { isEmpty } from 'lodash';

import Table from '../../components/global/Table.vue';
import QuickActionsMenu from './QuickActionsMenu.vue';
import AlertStatus from './AlertStatus.vue';
import AlertTypeIcon from './AlertTypeIcon.vue';
import TextButton from '../../components/global/TextButton.vue';
import LoadingIcon from '../../components/global/LoadingIcon.vue';
import FallBack from '../../components/global/FallBack.vue';
import ContactIcon from './ContactIcon.vue';
import AccessDenied from '../../components/global/AccessDenied.vue';
import { getAlertQuickActions, openInEase } from './helpers';
import { ALERTS, STATUS_ACTION_TYPES } from './constants';
import { VIEWS as MODAL_VIEWS } from './modals/constants';
import { truncateModalFields } from './helpers';

export default {
	name: 'ClientConnectDashboardList',
	data() {
		return {
			columnHeaders: ALERTS.TABLE_HEADERS.filter(
				(h) => h.name !== 'Owner',
			),
			STATUS_ACTION_TYPES,
			MODAL_VIEWS,
		};
	},
	components: {
		Table,
		QuickActionsMenu,
		AlertStatus,
		AlertTypeIcon,
		TextButton,
		LoadingIcon,
		FallBack,
		ContactIcon,
		AccessDenied,
	},
	props: {
		isToDo: Boolean,
		isDoneForYou: Boolean,
		isLoading: Boolean,
		dashboardAlerts: Array,
		error: String,
		noAlertsFound: Boolean,
		statusTotals: Object,
		blinkUnregistered: Boolean,
	},
	computed: {
		...mapState({
			mainProfileIsInternalUser: (state) =>
				state.profileSwitcher.mainProfileSelector.isInternalUser,
			loggedInUserId: (state) =>
				state.profileSwitcher.mainProfile.rolodexContactId,
		}),
		...mapGetters(['isImpersonated', 'hasAccessToClientConnect']),
	},
	methods: {
		/**
		 * when we use a function in the template that is imported from outside
		 * the component it needs to be included in the methods object
		 */
		// deepcode ignore VueIncorrectMethodType: see comment above
		isEmpty,
		truncateModalFields,
		openInEase,
		getDateString(date, alertType = '') {
			if (alertType === 'Birthday') {
				return new Date(date).toLocaleDateString('en-US', {
					month: 'long',
					day: 'numeric',
				});
			} else {
				return new Date(date).toLocaleDateString('en-US', {
					month: 'long',
					day: 'numeric',
					year: 'numeric',
				});
			}
		},
		// // Destructures the object passed in to only take the properties needed
		populateQuickActions({ contactId, statusId, loan, lead, type }) {
			let easeUrl = '';
			let isUwmLoan = false;
			let isLoanAlert = false;

			if (loan) {
				isLoanAlert = true;
				easeUrl = loan.easeLoanUrl;
				isUwmLoan = lead.uwmInd;
			}

			return getAlertQuickActions(
				true,
				contactId,
				statusId,
				this.mainProfileIsInternalUser,
				this.isImpersonated,
				this.loggedInUserId,
				isLoanAlert,
				easeUrl,
				isUwmLoan,
				type,
			);
		},
	},
};
</script>

<style lang="scss" scoped>
.access-denied {
	border: $b360-border-width-thin solid $b360-color--gray__lighter;
	border-radius: $b360-border-radius-base;
	background-color: $white;
}
.alerts-container {
	& + & {
		@include rem('margin-top', 32px);
	}

	/deep/ .custom-table:not(.full-border) {
		border-radius: 6px;
		border-bottom-left-radius: 0;
		border-bottom-right-radius: 0;
		border-bottom: none;
	}
}

.alerts-title {
	@include rem('margin', 0);
	@include typo--roboto__regular;
	display: flex;
	align-items: center;
	color: $subheader-color;
	font-size: $b360-font-size--largest;
}

.alerts-counter {
	background: $brand-blue;
	color: $white;
	font-weight: bold;
	line-height: 1;
	border-radius: 6px;
	display: flex;
	justify-content: center;
	align-items: center;
	@include rem('font-size', 14px);
	@include rem('margin-left', 11px);
	@include rem('padding', 0 5px);
	@include rem('height', 22px);
	@include rem('min-width', 22px);
}

.alerts-link {
	text-decoration: none;
	@include typo--roboto__medium;
	color: $brand-blue;
	transition: color 0.25s ease;

	&:hover,
	&:focus {
		color: $b360-color--blue__darker;
	}
}

.no-alerts-message {
	@include rem('margin-top', 15px);
}

.table {
	background: $white;
	border-bottom-left-radius: 0;
	border-bottom-right-radius: 0;

	&.full-border {
		@include rem('border-radius', 6px);
	}

	.table-head-row,
	.table-body-row {
		display: grid;
		grid-template-columns: repeat(2, minmax(5em, 1fr)) 20% 15% 5%;
		@include rem('grid-gap', 15px);
	}

	.table-body__type,
	.table-body__contact {
		display: flex;
	}

	.alert-icon,
	/deep/ .contact-icon {
		@include rem('margin-right', 15px);
	}

	.table-head__quick-actions {
		.quick-actions {
			display: none;
			cursor: default;
		}
	}

	.table-head__status,
	.table-body__status {
		text-align: center;
		margin: auto;
	}

	.table-body__quick-actions {
		display: flex;
		justify-content: flex-end;
	}

	.alert-type,
	.alert-type-info {
		display: flex;
		flex-direction: column;
		justify-content: space-between;
	}

	.type {
		color: $brand-blue;
		font-weight: 500;
		cursor: pointer;
	}

	@media only screen and (max-width: 830px) {
		.table-head-row,
		.table-body-row {
			grid-template-columns: 1fr minmax(5em, 2fr) 110px 34px;
		}
	}

	@media only screen and (max-width: 1200px) {
		.table-head__date,
		.table-body__date {
			display: none;
		}

		.table-head-row,
		.table-body-row {
			@include rem('grid-gap', 15px);
		}
	}

	@media only screen and (min-width: 1024px) and (max-width: 1200px) {
		.table-head__contact,
		.table-head__type {
			text-align: center;
		}
	}

	@media only screen and (min-width: 1024px) and (max-width: 1300px) {
		/deep/ .contact-icon {
			display: none;
		}
	}

	@media only screen and (min-width: 831px) and (max-width: 1200px) {
		.table-head-row,
		.table-body-row {
			grid-template-columns: 1fr minmax(5em, 2fr) 1fr 34px;
		}
	}

	@media only screen and (max-width: 720px) {
		/deep/ .contact-icon {
			display: none;
		}
	}

	@media only screen and (max-width: 640px) {
		.alert-icon {
			display: none;
		}
	}
}
</style>
